'use client';

import { type MouseEventHandler, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { Anchor } from '@zillow/constellation-10';

import { LucideIcon } from '@/src/components/LucideIcon';
import type { SeoLinkGroup } from '@/src/lib/api';
import { MIN_LG } from '@/src/lib/breakpoints';

import { caret, group, groupSummary, linkItem, linkList } from './seoFooter.styles';

type SeoFooterGroupProps = {
  group: SeoLinkGroup;
};

/**
 * One SEO footer group. Server-rendered OPEN so every link is in the SSR HTML
 * (crawlable) and desktop / no-JS visitors get the full list without depending
 * on JS. After mount we collapse it into an accordion only on mobile; desktop
 * stays a static, non-collapsible column: chevron hidden and mouse-inert via
 * CSS, plus the summary is dropped from the tab order with its toggle blocked
 * so keyboard users can't collapse a group that has no visible affordance. The
 * <details> is uncontrolled, so native toggling on mobile just works.
 */
export function SeoFooterGroup({ group: linkGroup }: SeoFooterGroupProps) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summaryRef = useRef<HTMLElement>(null);
  const isDesktopRef = useRef(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MIN_LG);
    const applyViewportState = () => {
      isDesktopRef.current = mediaQuery.matches;
      if (detailsRef.current) {
        // Desktop already renders open; this only collapses the mobile
        // accordion, and re-syncs if the viewport crosses the breakpoint.
        detailsRef.current.open = mediaQuery.matches;
      }
      if (summaryRef.current) {
        // Desktop has no visible toggle, so drop the summary from the tab
        // order; mobile keeps it focusable as the accordion control.
        summaryRef.current.tabIndex = mediaQuery.matches ? -1 : 0;
      }
    };
    applyViewportState();
    mediaQuery.addEventListener('change', applyViewportState);
    return () => mediaQuery.removeEventListener('change', applyViewportState);
  }, []);

  // A summary's Enter/Space activation dispatches a click, so this single guard
  // blocks both mouse and keyboard toggling on desktop (CSS pointer-events only
  // covers the mouse).
  const blockDesktopToggle: MouseEventHandler<HTMLElement> = (event) => {
    if (isDesktopRef.current) {
      event.preventDefault();
    }
  };

  return (
    <details ref={detailsRef} className={group} open>
      <summary ref={summaryRef} className={groupSummary} onClick={blockDesktopToggle}>
        <span>{linkGroup.header}</span>
        <LucideIcon icon={ChevronDown} size="sm" className={caret} />
      </summary>
      <ul className={linkList}>
        {linkGroup.links.map((link) => (
          <li key={link.path} className={linkItem}>
            <Anchor href={link.path}>{link.name}</Anchor>
          </li>
        ))}
      </ul>
    </details>
  );
}
